import { useEffect, useRef, useState } from 'react'
import { VideoTrack, AudioTrack } from 'livekit-client'
import { Mic, MicOff, Monitor, User, Volume2, Pin, PinOff } from 'lucide-react'

interface VideoTileProps {
  name: string
  videoTrack: VideoTrack | null
  audioTrack: AudioTrack | null // Kept for API compatibility but audio is handled by MeetingMiniView
  isMuted: boolean
  isCameraOff: boolean
  isScreenShare?: boolean
  isActiveSpeaker?: boolean
  isLocal?: boolean
  audioLevel?: number // 0-1 audio level
  onVolumeChange?: (volume: number) => void // Callback for volume control
  volume?: number // Current volume 0-1
  isPinned?: boolean // Whether this tile is pinned/enlarged
  onPin?: () => void // Callback for pin/unpin
}

export function VideoTile({
  name,
  videoTrack,
  audioTrack: _audioTrack, // Audio handled by MeetingMiniView
  isMuted,
  isCameraOff,
  isScreenShare = false,
  isActiveSpeaker = false,
  isLocal = false,
  audioLevel = 0,
  onVolumeChange,
  volume = 1,
  isPinned = false,
  onPin,
}: VideoTileProps) {
  const videoRef = useRef<HTMLVideoElement>(null)
  const [showVolumeSlider, setShowVolumeSlider] = useState(false)
  const volumeTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  // Keep volume slider open longer when hovering
  const handleVolumeMouseEnter = () => {
    if (volumeTimeoutRef.current) {
      clearTimeout(volumeTimeoutRef.current)
      volumeTimeoutRef.current = null
    }
    setShowVolumeSlider(true)
  }

  const handleVolumeMouseLeave = () => {
    // Delay hiding to make it easier to interact
    volumeTimeoutRef.current = setTimeout(() => {
      setShowVolumeSlider(false)
    }, 500)
  }

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (volumeTimeoutRef.current) {
        clearTimeout(volumeTimeoutRef.current)
      }
    }
  }, [])

  // Attach video track to video element
  useEffect(() => {
    if (videoTrack && videoRef.current) {
      videoTrack.attach(videoRef.current)
      return () => {
        videoTrack.detach(videoRef.current!)
      }
    }
  }, [videoTrack])

  // NOTE: Audio is now handled by MeetingMiniView to persist across tab navigation
  // This prevents audio from cutting out when navigating away from meetings tab

  return (
    <div
      className={`
        absolute inset-0 bg-[var(--bg-card)] rounded-xl overflow-hidden
        border-2 transition-all duration-200
        ${isActiveSpeaker ? 'border-[var(--primary)] shadow-lg shadow-[var(--primary)]/20' : 'border-[var(--border)]'}
        ${isScreenShare ? 'col-span-2 row-span-2' : ''}
      `}
    >
      {/* Video or Avatar */}
      {videoTrack && !isCameraOff ? (
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted={isLocal}
          className={`w-full h-full ${isScreenShare ? 'object-contain bg-black' : 'object-cover'} ${isLocal && !isScreenShare ? 'transform -scale-x-100' : ''}`}
        />
      ) : (
        <div className="w-full h-full flex items-center justify-center bg-[var(--bg-element)]">
          {isScreenShare ? (
            <Monitor size={48} className="text-[var(--text-muted)]" />
          ) : (
            <div className="w-20 h-20 rounded-full bg-[var(--primary)] flex items-center justify-center">
              <User size={40} className="text-white" />
            </div>
          )}
        </div>
      )}

      {/* Audio is handled by MeetingMiniView to persist across tab navigation */}

      {/* Audio level indicator bar (left side) */}
      {!isMuted && !isScreenShare && (
        <div className="absolute left-2 top-1/2 -translate-y-1/2 w-1 h-16 bg-black/30 rounded-full overflow-hidden">
          <div
            className="absolute bottom-0 w-full bg-green-500 rounded-full transition-all duration-75"
            style={{ height: `${Math.min(audioLevel * 100 * 3, 100)}%` }}
          />
        </div>
      )}

      {/* Top controls bar */}
      <div className="absolute top-2 right-2 z-10 flex items-center gap-1">
        {/* Pin button (for pinned items) */}
        {isPinned && onPin && (
          <button
            onClick={(e) => {
              e.stopPropagation()
              onPin()
            }}
            className="p-1.5 bg-[var(--primary)]/80 hover:bg-[var(--primary)] rounded-lg transition-colors"
            title="Unpin"
          >
            <PinOff size={14} className="text-white" />
          </button>
        )}

        {/* Volume control (for remote participants and screen shares) */}
        {onVolumeChange && (
          <div
            onMouseEnter={handleVolumeMouseEnter}
            onMouseLeave={handleVolumeMouseLeave}
          >
            <button
              className="p-1.5 bg-black/50 hover:bg-black/70 rounded-lg transition-colors"
              title="Adjust volume"
              onClick={(e) => e.stopPropagation()}
            >
              <Volume2 size={14} className="text-white" />
            </button>
            {showVolumeSlider && (
              <div
                className="absolute top-full right-0 mt-1 p-3 bg-black/90 rounded-lg min-w-[140px] shadow-xl"
                onMouseEnter={handleVolumeMouseEnter}
                onMouseLeave={handleVolumeMouseLeave}
                onClick={(e) => e.stopPropagation()}
              >
                <input
                  type="range"
                  min="0"
                  max="2"
                  step="0.05"
                  value={volume}
                  onChange={(e) => onVolumeChange(parseFloat(e.target.value))}
                  className="w-full h-2 bg-white/20 rounded-full appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:cursor-pointer [&::-webkit-slider-thumb]:shadow-md"
                />
                <div className="text-xs text-white/80 text-center mt-2 font-medium">
                  {Math.round(volume * 100)}%
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Bottom bar with name and mute indicator */}
      <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/70 to-transparent">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <span className="text-white text-sm font-medium truncate">
              {name}
              {isLocal && ' (You)'}
            </span>
            {/* Inline audio level bars */}
            {!isMuted && !isScreenShare && (
              <div className="flex items-end gap-0.5 h-3">
                {[0.15, 0.3, 0.5, 0.7].map((threshold, i) => (
                  <div
                    key={i}
                    className={`w-0.5 rounded-full transition-all duration-75 ${
                      audioLevel > threshold ? 'bg-green-500' : 'bg-white/30'
                    }`}
                    style={{ height: `${(i + 1) * 25}%` }}
                  />
                ))}
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            {isMuted ? (
              <div className="p-1.5 bg-red-500/80 rounded-full">
                <MicOff size={14} className="text-white" />
              </div>
            ) : (
              <div className="p-1.5 bg-[var(--bg-element)]/80 rounded-full">
                <Mic size={14} className="text-white" />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Screen share indicator */}
      {isScreenShare && (
        <div className="absolute top-3 left-3 px-2 py-1 bg-[var(--primary)] rounded-full">
          <span className="text-xs text-white font-medium flex items-center gap-1">
            <Monitor size={12} />
            Screen
          </span>
        </div>
      )}
    </div>
  )
}
